
import {
    AuditModel,
    AuditWorkplaceModel,
    AuditWorkplaceStationModel,
    PermissionTypeEnum,
    QueryOperator,
    TenantUserModel,
    WorkplaceModel,
} from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { auditsStore } from '../+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { cloneDeep, isEqual } from 'lodash';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { auditWorkplacesStore } from '@/libs/audit-workplaces/+state/store';
import WorkplaceStationList from '@/libs/audits/components/WorkplaceStationList.vue';

@Component({
    components: { WorkplaceStationList, ShiftTypeSelect },
})
export default class Plan extends Page {
    @Prop() tenantId!: string;
    @Prop() auditId!: string;
    districtId: string | null = null;
    audit: AuditModel | null = null;
    auditOriginal: AuditModel | null = null;
    basicRules = [(v: any) => !!v || this.$t('validation.thisFieldIsRequired')];
    deleteDialog = false;
    deleting = false;
    valid = false;
    loading = true;
    saving = false;
    hasChanges = false;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    auditWorkplaces: {
        workplaceId: string;
        auditWorkplace: AuditWorkplaceModel | null | undefined;
        workplace: WorkplaceModel | null | undefined;
    }[] = [];
    selectedWorkplaces: {
        workplaceId: string;
        auditWorkplace: AuditWorkplaceModel | null | undefined;
        workplace: WorkplaceModel | null | undefined;
    }[] = [];
    selectedWorkplacesOriginal: {
        workplaceId: string;
        auditWorkplace: AuditWorkplaceModel | null | undefined;
        workplace: WorkplaceModel | null | undefined;
    }[] = [];
    tableHeaders = [
        { text: this.$i18n.t('workplaces.name'), value: 'workplace.name', align: 'left', sortable: false, width: '30%' },
        {
            text: this.$i18n.t('workplaces.stations'),
            name: 'auditWorkplaceStations',
            value: 'auditWorkplaceStations',
            align: 'left',
            sortable: false,
        },
    ];

    @Ref() form!: any;

    // ✅ NOVÝ SYSTÉM: Kontroluje, zda je audit readonly podle kategorie-specifických oprávnění
    get isReadonly(): boolean {
        if (!this.audit || !this.audit.categoryId) return true;
        
        // Kontrola EditAllAudits s kontextovými oprávněními
        if (this.hasAccessToCategory(this.audit.categoryId, [PermissionTypeEnum.EditAllAudits])) {
            return false;
        }

        // Kontrola EditOwnedAudit s kontextovými oprávněními (musí být owner)
        if (this.audit.ownerId === this.tenantUser.id && 
            this.hasAccessToCategory(this.audit.categoryId, [PermissionTypeEnum.EditOwnedAudit])) {
            return false;
        }

        return true;
    }

    @Watch('audit', { deep: true })
    @Watch('selectedWorkplaces', { deep: true })
    questionGroupsChanged() {
        this.hasChanges = !isEqual(this.audit, this.auditOriginal);
        if (!this.hasChanges) {
            this.hasChanges = !isEqual(this.selectedWorkplaces, this.selectedWorkplacesOriginal);
        }
    }

    mounted() {
        this.audit = { ...auditsStore.useGetter(CrudGetter.Detail) } as AuditModel;
        this.auditOriginal = cloneDeep(this.audit);
        this.loadItems().then();
        this.loading = false;
    }

    public getAuditWorkplaces(): Promise<AuditWorkplaceModel[]> {
        return new Promise<AuditWorkplaceModel[]>((resolve, reject) => {
            auditWorkplacesStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload(undefined, [{ field: 'auditId', op: QueryOperator.Eq, comparand: this.auditId }])
            );
            this.subscribe(auditWorkplacesStore, CrudReponse.GetAll)
                .then((existingAuditWorkplaces: AuditWorkplaceModel[]) => {
                    resolve(existingAuditWorkplaces);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public getWorkplaces(): Promise<AuditWorkplaceModel[]> {
        return new Promise<AuditWorkplaceModel[]>((resolve, reject) => {
            workplacesStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload({
                    field: 'name',
                    index: 1,
                    order: 'asc',
                })
            );
            this.subscribe(workplacesStore, CrudReponse.GetAll)
                .then((existingAuditWorkplaces: WorkplaceModel[]) => {
                    resolve(existingAuditWorkplaces);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public deleteAllPromise(payload: AuditWorkplaceModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.deleteAllPromisePop(payload, resolve, reject);
        });
    }

    public saveAllPromise(payload: AuditWorkplaceModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.saveAllPromisePop(payload, resolve, reject);
        });
    }

    private save() {
        this.saving = true;
        auditsStore.dispatch(CrudAction.Update, { item: this.audit });
        this.subscribe(auditsStore, CrudReponse.Update)
            .then((e: AuditModel) => {
                this.audit = e;
                this.getAuditWorkplaces()
                    .then((existingAuditWorkplaces: AuditWorkplaceModel[]) => {
                        let selectedWorkplaces = this.selectedWorkplaces;
                        if (!this.audit!.useWorkplaceLimitation) {
                            selectedWorkplaces = [];
                        }
                        const auditWorkplacesToDelete = existingAuditWorkplaces.filter(
                            (x) => selectedWorkplaces.find((n) => n.workplaceId == x.workplaceId) == null
                        );
                        const auditWorkplacesToInsertOrUpdate = selectedWorkplaces.map((x) => {
                            return {
                                id: x.auditWorkplace?.id ?? Guid.EmptyGuid(),
                                auditId: this.auditId,
                                workplaceId: x.workplaceId,
                                useStationLimitation: x.auditWorkplace?.useStationLimitation ?? false,
                                auditWorkplaceStations: x.auditWorkplace?.useStationLimitation
                                    ? x.auditWorkplace?.auditWorkplaceStations ?? []
                                    : [],
                            } as AuditWorkplaceModel;
                        });
                        this.deleteAllPromise(auditWorkplacesToDelete)
                            .then(() => {
                                this.saveAllPromise(auditWorkplacesToInsertOrUpdate)
                                    .then(() => {
                                        this.loadItems()
                                            .then(() => {
                                                this.saving = false;
                                            })
                                            .catch((e: any) => {
                                                this.saving = false;
                                                console.error(e);
                                            });
                                    })
                                    .catch((e: any) => {
                                        this.saving = false;
                                        console.error(e);
                                    });
                            })
                            .catch((e: any) => {
                                this.saving = false;
                                console.error(e);
                            });
                    })
                    .catch((e: any) => {
                        this.saving = false;
                        console.error(e);
                    });
            })
            .catch((e: any) => {
                this.saving = false;
                console.error(e);
            });
    }

    private loadItems(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getAuditWorkplaces()
                .then((existingAuditWorkplaces: AuditWorkplaceModel[]) => {
                    this.getWorkplaces()
                        .then((workplaces: WorkplaceModel[]) => {
                            const deletedWorkplaces = existingAuditWorkplaces!.filter(
                                (x) => workplaces.find((n) => n.id == x.workplaceId) == null
                            );
                            this.auditWorkplaces = [
                                ...deletedWorkplaces.map((x) => {
                                    return {
                                        workplaceId: x.workplaceId!,
                                        auditWorkplace: x,
                                        workplace: x.workplace,
                                    };
                                }),
                            ];
                            this.selectedWorkplaces = [
                                ...deletedWorkplaces.map((x) => {
                                    return {
                                        workplaceId: x.workplaceId!,
                                        auditWorkplace: null,
                                        workplace: null,
                                    };
                                }),
                            ];
                            this.auditWorkplaces = [
                                ...this.auditWorkplaces,
                                ...workplaces.map((x) => {
                                    return {
                                        workplaceId: x.id!,
                                        auditWorkplace: existingAuditWorkplaces!.find((n) => n.workplaceId == x.id) ?? {
                                            id: Guid.EmptyGuid(),
                                        },
                                        workplace: x,
                                    };
                                }),
                            ];
                            this.selectedWorkplaces = [
                                ...existingAuditWorkplaces!.map((x) => {
                                    return {
                                        workplaceId: x.workplaceId!,
                                        auditWorkplace: x,
                                        workplace: x.workplace,
                                    };
                                }),
                            ];
                            this.selectedWorkplacesOriginal = cloneDeep(this.selectedWorkplaces);
                            resolve(undefined);
                        })
                        .catch((e: any) => reject(e));
                })
                .catch((e: any) => reject(e));
        });
    }

    private deleteAllPromisePop(payload: AuditWorkplaceModel[], resolve: (value: any) => void, reject: (reason?: any) => void) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id != Guid.EmptyGuid()) {
                auditWorkplacesStore.dispatch(CrudAction.Delete, { id: payloadItem.id });
                this.subscribe(auditWorkplacesStore, CrudReponse.Delete)
                    .then((e: any) => {
                        payload.shift();
                        this.deleteAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }

    private saveAllPromisePop(payload: AuditWorkplaceModel[], resolve: (value: any) => void, reject: (reason?: any) => void) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id == Guid.EmptyGuid()) {
                auditWorkplacesStore.dispatch(CrudAction.Create, { item: payloadItem });
                this.subscribe(auditWorkplacesStore, CrudReponse.Create)
                    .then((e: any) => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            } else {
                auditWorkplacesStore.dispatch(CrudAction.Update, { item: payloadItem });
                this.subscribe(auditWorkplacesStore, CrudReponse.Update)
                    .then((e: any) => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }

    isSelected(workplaceId: string): boolean {
        return this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId) != null;
    }

    onItemSelected(e: any, a: any) {
        e.item!.auditWorkplace!.useStationLimitation = false;
        e.item!.auditWorkplace!.auditWorkplaceStations = [];
    }

    getUseStationLimitation(workplaceId: string) {
        return this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId)?.auditWorkplace?.useStationLimitation ?? false;
    }

    getAuditWorkplaceStations(workplaceId: string) {
        return this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId)?.auditWorkplace?.auditWorkplaceStations ?? [];
    }

    setUseStationLimitation(workplaceId: string, value: boolean) {
        const selectedWorkplace = this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId);
        if (selectedWorkplace && selectedWorkplace.auditWorkplace) {
            selectedWorkplace.auditWorkplace!.useStationLimitation = value;
        }
        this.$forceUpdate();
    }

    setAuditWorkplaceStations(workplaceId: string, auditWorkplaceStations: AuditWorkplaceStationModel[]) {
        const selectedWorkplace = this.selectedWorkplaces.find((x) => x.workplaceId == workplaceId);
        if (selectedWorkplace && selectedWorkplace.auditWorkplace) {
            selectedWorkplace.auditWorkplace!.auditWorkplaceStations = auditWorkplaceStations;
        }
    }
}
